import React from 'react';

import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import c from 'classnames';

import LazyLoadedImage from 'common/LazyLoadedImage';
import { Span } from 'common/ui/Text';
import styles from 'css-module/components/ui/_DropdownMenu.module.scss';

import type { LucideProps } from 'lucide-react';

// see radix docs for more information on props: https://www.radix-ui.com/primitives/docs/components/dropdown-menu
type Props = {
  align?: 'center' | 'end' | 'start';
  alignOffset?: number;
  children: React.ReactNode | React.ReactNode[];
  disabled?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onPointerDownOutside?: (e: CustomEvent<{ originalEvent: PointerEvent }>) => void;
  open?: boolean;
  side?: 'bottom' | 'left' | 'right' | 'top';
  sideOffset?: number;
  trigger: React.ReactNode;
};

function DropdownMenu({
  align,
  alignOffset,
  children,
  disabled,
  defaultOpen,
  onOpenChange,
  onPointerDownOutside,
  open,
  side,
  sideOffset,
  trigger,
}: Props) {
  return (
    <RadixDropdownMenu.Root
      defaultOpen={defaultOpen}
      modal={false}
      onOpenChange={onOpenChange}
      open={open}>
      <RadixDropdownMenu.Trigger disabled={disabled} className={styles.trigger} asChild>
        {trigger}
      </RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          align={align}
          alignOffset={alignOffset}
          className={c(styles.content, 'ignoreModalClickAway')}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={onPointerDownOutside}
          side={side}
          sideOffset={sideOffset}>
          {children}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}

type IconProps = {
  className?: string;
  icon?: React.ComponentType<LucideProps>;
  iconSize?: number;
  iconStrokeWidth?: number;
  imageIcon?: string;
};

function MenuItemIcon({ icon: Icon, iconSize = 14, iconStrokeWidth = 1.5, imageIcon }: IconProps) {
  if (Icon) {
    return <Icon className={styles.itemIcon} size={iconSize} strokeWidth={iconStrokeWidth} />;
  }

  if (imageIcon) {
    return <LazyLoadedImage className={styles.itemImageIcon} src={imageIcon} alt="icon" />;
  }

  return null;
}

type ItemProps = {
  disabled?: boolean;
  icon?: React.ComponentType<LucideProps>;
  iconSize?: number;
  iconStrokeWidth?: number;
  imageIcon?: string;
  label: string;
  onSelect: () => void;
  selected?: boolean;
  variant?: 'destructive';
};
function MenuItem({
  disabled,
  icon,
  iconSize,
  iconStrokeWidth,
  imageIcon,
  label,
  onSelect,
  selected,
  variant,
}: ItemProps) {
  return (
    <RadixDropdownMenu.Item
      className={c(styles.item, {
        [styles.destructive]: variant === 'destructive',
        [styles.selected]: selected,
      })}
      disabled={disabled}
      onSelect={onSelect}>
      <MenuItemIcon
        icon={icon}
        iconSize={iconSize}
        iconStrokeWidth={iconStrokeWidth}
        imageIcon={imageIcon}
      />
      <Span className={styles.itemLabel} variant="bodyMd">
        {label}
      </Span>
    </RadixDropdownMenu.Item>
  );
}

DropdownMenu.MenuItem = MenuItem;

export default DropdownMenu;
